/* Load before theme scripts. Enable only the native Shopify newsletter forms
 * that have been inspected and tested. Never put either secret in this file. */
(() => {
  'use strict';
  const ENDPOINT = 'https://dp-nieuwsbrief-koppeling.social-b74.workers.dev/newsletter';
  const PUBLIC_SITE_KEY = '0x4AAAAAAFKgq0iuw4kg3UPh';
  const SELECTOR = 'form[data-newsletter-bridge="enabled"]';
  const WARNING_KEY = 'dp-newsletter-discount-check-warning';
  const WARNING = 'De kortingscontrole lukte niet. Ontvang je geen kortingsmail na je aanmelding, neem dan contact met ons op.';
  const FAILURE_STAGES = new Set(['api-load', 'token-render', 'token-error', 'token-expired', 'token-timeout', 'endpoint-request', 'endpoint-json', 'endpoint-rejected']);
  const states = new WeakMap();
  const passThrough = new WeakSet();
  let apiPromise;
  let apiLoadAttempt = 0;

  function statusElement(form) {
    let element = form.querySelector('[data-newsletter-bridge-status]');
    if (!element) {
      element = document.createElement('p');
      element.dataset.newsletterBridgeStatus = '';
      element.setAttribute('role', 'status');
      element.setAttribute('aria-live', 'polite');
      form.append(element);
    }
    return element;
  }

  function apiReady() {
    if (!apiPromise) apiPromise = new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error('Verification unavailable')), 10000);
      const fail = () => { clearTimeout(timer); reject(new Error('Verification unavailable')); };
      const callbackName = `dpNewsletterTurnstileOnload${++apiLoadAttempt}`;
      // Define the documented API onload callback BEFORE inserting the script.
      // DOM script.onload can fire before Turnstile's own initialization, and
      // calling ready() with this asynchronous loader is explicitly disallowed.
      window[callbackName] = () => {
        const api = window.turnstile;
        clearTimeout(timer);
        if (api && typeof api.render === 'function') resolve(api);
        else fail();
      };
      const script = document.createElement('script');
      script.src = `https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=${callbackName}`;
      script.async = true;
      script.onerror = fail;
      document.head.append(script);
    }).catch(error => { apiPromise = undefined; throw error; });
    return apiPromise;
  }

  async function freshToken(form, state, diagnostic) {
    const api = await apiReady();
    diagnostic.stage = 'token-render';
    const sitekey = form.dataset.turnstileSitekey || PUBLIC_SITE_KEY;
    if (!sitekey) throw new Error('Verification unavailable');
    if (state.widget !== undefined) api.remove(state.widget);
    let container = form.querySelector('[data-newsletter-turnstile]');
    if (!container) {
      container = document.createElement('div');
      container.dataset.newsletterTurnstile = '';
      form.append(container);
    }
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => { diagnostic.stage = 'token-timeout'; reject(new Error('Verification timed out')); }, 60000);
      const fail = stage => { diagnostic.stage = stage; clearTimeout(timer); reject(new Error('Verification unavailable')); };
      try {
        state.widget = api.render(container, {
          sitekey,
          action: 'newsletter',
          theme: 'auto',
          size: 'flexible',
          appearance: 'interaction-only',
          'response-field': false,
          callback: token => { clearTimeout(timer); resolve(token); },
          'error-callback': () => fail('token-error'),
          'expired-callback': () => fail('token-expired'),
          'timeout-callback': () => fail('token-timeout'),
        });
      } catch { fail('token-render'); }
    });
  }

  function safeDiagnostic(value = {}) {
    return {
      stage: FAILURE_STAGES.has(value.stage) ? value.stage : 'unknown',
      httpStatus: Number.isInteger(value.httpStatus) && value.httpStatus >= 100 && value.httpStatus <= 599 ? value.httpStatus : null,
    };
  }

  function logFailure(diagnostic) {
    const safe = safeDiagnostic(diagnostic);
    // Fixed categories/numeric HTTP status only. Never log caught errors,
    // addresses, tokens, provider responses, or other request data.
    console.warn(`[D&P nieuwsbrief] kortingscontrole mislukt: stage=${safe.stage}; http=${safe.httpStatus ?? 'none'}`);
    return safe;
  }

  function rememberWarning(form, diagnostic) {
    statusElement(form).textContent = WARNING;
    const safe = logFailure(diagnostic);
    try {
      // No email address, token, membership status, or other personal data.
      sessionStorage.setItem(WARNING_KEY, JSON.stringify({ formId: form.id || '', expires: Date.now() + 120000, ...safe }));
    } catch { /* Storage being unavailable must not prevent a native signup. */ }
  }

  function restoreWarning() {
    try {
      const value = sessionStorage.getItem(WARNING_KEY);
      if (!value) return;
      sessionStorage.removeItem(WARNING_KEY);
      const warning = JSON.parse(value);
      if (warning.expires < Date.now()) return;
      logFailure(warning);
      const matching = warning.formId ? document.getElementById(warning.formId) : null;
      const form = matching?.matches(SELECTOR) ? matching : document.querySelector(SELECTOR);
      if (form) statusElement(form).textContent = WARNING;
    } catch { /* Nonessential feedback only. */ }
  }

  document.addEventListener('submit', async event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches(SELECTOR)) return;
    if (passThrough.has(form)) { passThrough.delete(form); return; }
    // Stop theme submit handlers until the Mailchimp read/write finishes. Never
    // fire-and-forget this request: Shopify sync would race the membership read.
    event.preventDefault();
    event.stopImmediatePropagation();
    let state = states.get(form);
    if (!state) { state = { busy: false }; states.set(form, state); }
    if (state.busy || !form.reportValidity()) return;
    const emailInput = form.querySelector('input[name="contact[email]"]');
    if (!emailInput) return;
    const checkedEmail = emailInput.value.trim();
    const wasReadOnly = emailInput.readOnly;
    emailInput.readOnly = true;
    state.busy = true;
    const submitter = event.submitter;
    const buttons = [...form.querySelectorAll('button[type="submit"], input[type="submit"], button:not([type])')];
    const disabled = buttons.map(button => button.disabled);
    buttons.forEach(button => { button.disabled = true; });
    form.setAttribute('aria-busy', 'true');
    statusElement(form).textContent = 'Even controleren…';
    const diagnostic = { stage: 'api-load', httpStatus: null };
    try {
      const token = await freshToken(form, state, diagnostic);
      diagnostic.stage = 'endpoint-request';
      const response = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'omit',
        cache: 'no-store',
        redirect: 'error',
        referrerPolicy: 'no-referrer',
        signal: AbortSignal.timeout(20000),
        body: JSON.stringify({ email: checkedEmail, token }),
      });
      diagnostic.httpStatus = response.status;
      diagnostic.stage = 'endpoint-json';
      const result = await response.json();
      diagnostic.stage = 'endpoint-rejected';
      if (!response.ok || result.success !== true) throw new Error('Discount check unavailable');
      statusElement(form).textContent = '';
      try { sessionStorage.removeItem(WARNING_KEY); } catch { /* Optional storage. */ }
    } catch {
      // Preserve Shopify's existing signup during an outage, while honestly
      // indicating that the extra discount check could not be confirmed.
      rememberWarning(form, diagnostic);
    } finally {
      if (state.widget !== undefined && window.turnstile) {
        try { window.turnstile.remove(state.widget); } catch { /* Best effort. */ }
        state.widget = undefined;
      }
      buttons.forEach((button, index) => { button.disabled = disabled[index]; });
      emailInput.readOnly = wasReadOnly;
      form.removeAttribute('aria-busy');
      state.busy = false;
    }
    // requestSubmit preserves native validation, the original submitter, and
    // Shopify/theme/CAPTCHA submit handlers. A single bypass prevents recursion.
    if (emailInput.value.trim().toLowerCase() !== checkedEmail.toLowerCase()) {
      statusElement(form).textContent = 'Je e-mailadres is gewijzigd. Klik opnieuw op aanmelden.';
      return;
    }
    passThrough.add(form);
    try {
      if (submitter?.form === form && !submitter.disabled) form.requestSubmit(submitter);
      else form.requestSubmit();
    } finally { passThrough.delete(form); }
  }, true);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', restoreWarning, { once: true });
  else restoreWarning();
})();
